import { Fragment } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderAddressView } from "@/lib/api/types";

type Props = {
  /** Structured delivery address, or `null` for legacy orders. */
  address: OrderAddressView | null;
  /** Composed single-line address shown when no structured address exists. */
  fallback: string;
  /** Extra classes merged onto the wrapper. */
  className?: string;
};

type LabeledField = {
  labelKey: string;
  value: string;
};

/**
 * Renders the order delivery address as a labeled per-field breakdown
 * (Area / Street / Building / Floor / Apartment / Landmark). Optional fields
 * are omitted when empty. Falls back to the composed `buyerAddress` string for
 * legacy orders that predate structured addresses.
 */
export function OrderAddressBlock({ address, fallback, className }: Props) {
  const t = useTranslations();

  if (!address) {
    return (
      <span
        className={[
          "text-[color:var(--text-primary)]",
          className ?? "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {fallback.trim() ? fallback : t(keys.common.empty)}
      </span>
    );
  }

  const fields: LabeledField[] = [
    { labelKey: keys.orders.addressAreaLabel, value: address.area },
    { labelKey: keys.orders.addressStreetLabel, value: address.street },
    { labelKey: keys.orders.addressBuildingLabel, value: address.building },
    { labelKey: keys.orders.addressFloorLabel, value: address.floor ?? "" },
    {
      labelKey: keys.orders.addressApartmentLabel,
      value: address.apartment ?? "",
    },
    {
      labelKey: keys.orders.addressLandmarkLabel,
      value: address.landmark ?? "",
    },
  ].filter((field) => field.value.trim() !== "");

  return (
    <dl
      className={[
        "mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {fields.map((field) => (
        <Fragment key={field.labelKey}>
          <dt className="text-xs font-medium text-[color:var(--text-muted)]">
            {t(field.labelKey)}
          </dt>
          <dd className="min-w-0 break-words text-sm text-[color:var(--text-primary)]">
            {field.value}
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}
